class PredictiveSearch extends SearchForm {
	constructor() {
		super();
		this.cachedResults = {};
		this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
		this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
		this.isOpen = false;
		this.abortController = new AbortController();
		this.searchTerm = '';

		this.fieldLabel =
			this.input.labels && this.input.labels.length > 0
				? this.input.labels[0]
				: this.input.closest('.field')?.querySelector('.field__label');
		this.originalLabelText = this.fieldLabel
			? window.predictiveSearch.defaultPlaceholder
			: window.predictiveSearch.defaultPlaceholder;
		this.animatedPlaceholderTexts = window.predictiveSearch.animatedPlaceholderTexts;
		this.animationTimeout = null;
		this.currentPlaceholderIndex = 0;
		this.isAnimating = false;
		this.typingSpeed = 100;
		this.deleteSpeed = 70;
		this.delayAfterTyping = 1000;
		this.delayBeforeTypingNext = 500;
		this.typingPhase = 'TYPING_SUFFIX';

		if (this.fieldLabel) {
			this.fieldLabel.style.transition = 'opacity 0.3s ease-in-out';
		}

		this.setupEventListeners();
	}

	setupEventListeners() {
		this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

		this.input.addEventListener('focus', this.onFocus.bind(this));
		this.addEventListener('focusout', this.onFocusOut.bind(this));
		this.input.addEventListener('input', this.onInput.bind(this));
		this.addEventListener('keydown', this.onKeydown.bind(this));

		if (this.input.value === '' && this.fieldLabel) {
			this.startPlaceholderAnimation();
		} else if (this.fieldLabel) {
			this.fieldLabel.style.opacity = '0';
		}
	}

	onInput() {
		const currentQuery = this.getQuery();
		if (currentQuery.length > 0) {
			this.stopPlaceholderAnimation(true);
		} else {
			this.startPlaceholderAnimation();
		}
		this.onChange();
	}

	_typeEffect() {
		if (!this.fieldLabel || !this.isAnimating) return;

		const currentAnimatedText = this.animatedPlaceholderTexts[this.currentPlaceholderIndex];
		let displayedSuffix = this.fieldLabel.textContent.startsWith(this.originalLabelText + ' ')
			? this.fieldLabel.textContent.substring(this.originalLabelText.length + 1)
			: '';

		switch (this.typingPhase) {
			case 'TYPING_SUFFIX':
				if (displayedSuffix.length < currentAnimatedText.length) {
					this.fieldLabel.textContent =
						this.originalLabelText + ' ' + currentAnimatedText.slice(0, displayedSuffix.length + 1);
					this.animationTimeout = setTimeout(() => this._typeEffect(), this.typingSpeed);
				} else {
					this.typingPhase = 'SUFFIX_TYPED_PAUSE';
					this.animationTimeout = setTimeout(() => this._typeEffect(), this.delayAfterTyping);
				}
				break;

			case 'SUFFIX_TYPED_PAUSE':
				this.typingPhase = 'DELETING_SUFFIX';
				this.animationTimeout = setTimeout(() => this._typeEffect(), 0);
				break;

			case 'DELETING_SUFFIX':
				if (displayedSuffix.length > 0) {
					this.fieldLabel.textContent = this.originalLabelText + ' ' + displayedSuffix.slice(0, -1);
					this.animationTimeout = setTimeout(() => this._typeEffect(), this.deleteSpeed);
				} else {
					this.typingPhase = 'SUFFIX_DELETED_PAUSE_BEFORE_NEXT';
					this.animationTimeout = setTimeout(() => this._typeEffect(), this.delayBeforeTypingNext);
				}
				break;

			case 'SUFFIX_DELETED_PAUSE_BEFORE_NEXT':
				this.currentPlaceholderIndex =
					(this.currentPlaceholderIndex + 1) % this.animatedPlaceholderTexts.length;
				this.fieldLabel.textContent = this.originalLabelText + ' ';
				this.typingPhase = 'TYPING_SUFFIX';
				this.animationTimeout = setTimeout(() => this._typeEffect(), 0);
				break;
		}
	}

	startPlaceholderAnimation() {
		if (!this.fieldLabel || this.isAnimating || this.input.value !== '') return;

		this.isAnimating = true;
		this.fieldLabel.style.opacity = '1';
		this.fieldLabel.textContent = this.originalLabelText + ' ';
		this.typingPhase = 'TYPING_SUFFIX';

		clearTimeout(this.animationTimeout);
		this._typeEffect();
	}

	stopPlaceholderAnimation(hideLabel = true) {
		if (!this.fieldLabel) return;

		this.isAnimating = false;
		clearTimeout(this.animationTimeout);
		this.animationTimeout = null;

		if (hideLabel) {
			this.fieldLabel.style.opacity = '0';
		} else {
			this.fieldLabel.textContent = this.originalLabelText;
			this.fieldLabel.style.opacity = '1';
		}
	}

	getQuery() {
		return this.input.value.trim();
	}

	onChange() {
		super.onChange();
		const newSearchTerm = this.getQuery();

		if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
			this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
		}

		this.updateSearchForTerm(this.searchTerm, newSearchTerm);

		this.searchTerm = newSearchTerm;

		if (!this.searchTerm.length) {
			this.close(true);
			return;
		}

		this.getSearchResults(this.searchTerm);
	}

	onFormSubmit(event) {
		if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a'))
			event.preventDefault();
	}

	onFormReset(event) {
		super.onFormReset(event);
		if (super.shouldResetForm()) {
			this.searchTerm = '';
			this.abortController.abort();
			this.abortController = new AbortController();
			this.closeResults(true);
			if (this.getQuery().length === 0) {
				this.startPlaceholderAnimation();
			}
		}
	}

	onFocus() {
		const currentSearchTerm = this.getQuery();

		if (currentSearchTerm.length === 0) {
			this.startPlaceholderAnimation();
		} else {
			this.stopPlaceholderAnimation(true);
		}

		if (this.searchTerm !== currentSearchTerm) {
			this.onChange();
		} else if (this.getAttribute('results') === 'true' && currentSearchTerm.length > 0) {
			this.open();
		} else if (currentSearchTerm.length > 0) {
			this.getSearchResults(this.searchTerm);
		}
	}

	onFocusOut() {
		setTimeout(() => {
			if (!this.contains(document.activeElement)) this.close();
		});
	}

	onKeyup(event) {
		if (this.getQuery().length === 0 && (event.key === 'Backspace' || event.key === 'Delete')) {
			this.startPlaceholderAnimation();
		}

		if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
			event.preventDefault();
		}

		if (!this.getQuery().length && !['Enter', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
			this.close(true);
		}

		switch (event.code) {
			case 'ArrowUp':
				this.switchOption('up');
				break;
			case 'ArrowDown':
				this.switchOption('down');
				break;
			case 'Enter':
				this.selectOption();
				break;
		}
	}

	onKeydown(event) {
		if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
			event.preventDefault();
		}
	}

	updateSearchForTerm(previousTerm, newTerm) {
		const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
		if (!searchForTextElement) return;

		const currentButtonText = searchForTextElement.innerText;
		if (!currentButtonText) return;

		if (typeof previousTerm !== 'string' || previousTerm === '' || previousTerm === newTerm) {
			return;
		}

		const escapedPreviousTerm = this._escapeRegExp(previousTerm);
		const countRegex = new RegExp(escapedPreviousTerm, 'g');
		const matches = currentButtonText.match(countRegex);

		if (matches && matches.length === 1) {
			const replaceRegex = new RegExp(escapedPreviousTerm);
			searchForTextElement.innerText = currentButtonText.replace(replaceRegex, newTerm || '');
		}
	}

	_escapeRegExp(string) {
		if (typeof string !== 'string') return '';
		return string.replace(/[.*+?^${}()|[\\]\\]/g, '\\$&');
	}

	switchOption(direction) {
		if (!this.getAttribute('open')) return;

		const moveUp = direction === 'up';
		const selectedElement = this.querySelector('[aria-selected="true"]');

		const allVisibleElements = Array.from(
			this.querySelectorAll('li, button.predictive-search__item'),
		).filter((element) => element.offsetParent !== null);
		let activeElementIndex = 0;

		if (moveUp && !selectedElement) return;

		let selectedElementIndex = -1;
		let i = 0;

		while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
			if (allVisibleElements[i] === selectedElement) {
				selectedElementIndex = i;
			}
			i++;
		}

		this.statusElement.textContent = '';

		if (!moveUp && selectedElement) {
			activeElementIndex =
				selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
		} else if (moveUp) {
			activeElementIndex =
				selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
		}

		if (activeElementIndex === selectedElementIndex) return;

		const activeElement = allVisibleElements[activeElementIndex];

		activeElement.setAttribute('aria-selected', true);
		if (selectedElement) selectedElement.setAttribute('aria-selected', false);

		this.input.setAttribute('aria-activedescendant', activeElement.id);
	}

	selectOption() {
		const selectedOption = this.querySelector(
			'[aria-selected="true"] a, button[aria-selected="true"]',
		);

		if (selectedOption) selectedOption.click();
	}

	getSearchResults(searchTerm) {
		const queryKey = searchTerm.replace(' ', '-').toLowerCase();
		this.setLiveRegionLoadingState();

		if (this.cachedResults[queryKey]) {
			this.renderSearchResults(this.cachedResults[queryKey]);
			return;
		}

		fetch(
			`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&resources[limit_scope]=each&resources[limit]=5&section_id=predictive-search`,
			{
				signal: this.abortController.signal,
			},
		)
			.then((response) => {
				if (!response.ok) {
					var error = new Error(response.status);
					this.close();
					throw error;
				}

				return response.text();
			})
			.then((text) => {
				const resultsMarkup = new DOMParser()
					.parseFromString(text, 'text/html')
					.querySelector('#shopify-section-predictive-search').innerHTML;
				this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
					predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
				});
				this.renderSearchResults(resultsMarkup);
			})
			.catch((error) => {
				if (error?.code === 20) {
					return;
				}
				this.close();
				throw error;
			});
	}

	setLiveRegionLoadingState() {
		this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
		this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

		this.setLiveRegionText(this.loadingText);
		this.setAttribute('loading', true);
	}

	setLiveRegionText(statusText) {
		this.statusElement.setAttribute('aria-hidden', 'false');
		this.statusElement.textContent = statusText;

		setTimeout(() => {
			this.statusElement.setAttribute('aria-hidden', 'true');
		}, 1000);
	}

	renderSearchResults(resultsMarkup) {
		this.predictiveSearchResults.innerHTML = resultsMarkup;
		this.setAttribute('results', true);

		this.setLiveRegionResults();
		this.open();
	}

	setLiveRegionResults() {
		this.removeAttribute('loading');
		this.setLiveRegionText(
			this.querySelector('[data-predictive-search-live-region-count-value]').textContent,
		);
	}

	getResultsMaxHeight() {
		this.resultsMaxHeight =
			window.innerHeight -
			document.querySelector('.section-header')?.getBoundingClientRect().bottom;
		return this.resultsMaxHeight;
	}

	open() {
		this.predictiveSearchResults.style.maxHeight =
			this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
		this.setAttribute('open', true);
		this.input.setAttribute('aria-expanded', true);
		this.isOpen = true;
		this.input.focus();
	}

	close(clearSearchTerm = false) {
		this.closeResults(clearSearchTerm);
		this.isOpen = false;

		if (clearSearchTerm && this.input.value === '') {
			this.startPlaceholderAnimation();
		} else if (this.input.value.length > 0) {
			this.stopPlaceholderAnimation(true);
		}
	}

	closeResults(clearSearchTerm = false) {
		if (clearSearchTerm) {
			this.input.value = '';
			this.removeAttribute('results');
		}
		const selected = this.querySelector('[aria-selected="true"]');

		if (selected) selected.setAttribute('aria-selected', false);

		this.input.setAttribute('aria-activedescendant', '');
		this.removeAttribute('loading');
		this.removeAttribute('open');
		this.input.setAttribute('aria-expanded', false);
		this.resultsMaxHeight = false;
		this.predictiveSearchResults.removeAttribute('style');
	}
}

customElements.define('predictive-search', PredictiveSearch);
